import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { server, tinyApiKey } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
import { motion } from "framer-motion";
import { FiArrowLeft, FiEdit3, FiInfo, FiCheck, FiRefreshCw, FiLayers } from "react-icons/fi";

export default function EditDoc() {
  const navigate = useNavigate();
  const [docDetails, setDocDetails] = useState("");
  const location = useLocation();
  const docId = location.state?.docId || null;
  
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  
  const [titleError, setTitleError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [contentError, setContentError] = useState("");
  const [changeSummaryError, setChangeSummaryError] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${server}/categories/all`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setCategories(data?.data || []);
      } catch (err) {
        toast.error("Failed to load categories list");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const getDocumentDetails = useCallback(async () => {
    try {
      if (!docId) return null;
      setFetching(true);
      const { data } = await axios.get(`${server}/docs/getById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          docsId: docId || null,
        },
      });

      const doc = data?.data;
      setDocDetails(doc);
      setTitle(doc?.title || "");
      setContent(doc?.content || "");
      setCategory(doc?.category || "");
      setFetching(false);
    } catch (e) {
      setFetching(false);
      toast.error(e?.response?.data || e.response);
    }
  }, [docId]);

  useEffect(() => {
    getDocumentDetails();
  }, [getDocumentDetails]);

  const handleUpdateDoc = async () => {
    let isValid = true;

    if (!title.trim()) {
      setTitleError("Title is required");
      isValid = false;
    } else {
      setTitleError("");
    }

    if (!category.trim()) {
      setCategoryError("Category is required");
      isValid = false;
    } else {
      setCategoryError("");
    }

    const strippedContent = content.replace(/<[^>]*>/g, "").trim();
    if (!strippedContent) {
      setContentError("Document content is required");
      isValid = false;
    } else {
      setContentError("");
    }

    if (!changeSummary.trim()) {
      setChangeSummaryError("Change summary is required to track version revisions");
      isValid = false;
    } else {
      setChangeSummaryError("");
    }

    if (!isValid) return;
    setLoading(true);

    try {
      const { data: resData } = await axios.patch(
        `${server}/docs/updateById`,
        content.toString() || "",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          params: {
            docsId: docId || null,
            title: title.trim(),
            coverImageUrl: "",
            changeSummary: changeSummary.trim(),
            category: category.trim(),
          },
        }
      );
      toast.success(resData?.message || "Document updated successfully!");
      setLoading(false);
      navigate(-1);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || e?.message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 font-sans">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium text-sm transition mb-6 group"
        >
          <FiArrowLeft className="group-hover:-translate-x-1 transition duration-200" />
          Cancel and return
        </button>

        {/* Title / Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
              <span className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                <FiEdit3 className="text-2xl" />
              </span>
              Edit Document
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Modify the document contents and track the version history changes.
            </p>
          </div>

          {!fetching && (
            <div className="flex gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 text-xs font-bold font-mono">
                <FiRefreshCw className="animate-spin-slow" />
                Current: v{docDetails.version}
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 rounded-xl text-green-700 text-xs font-bold font-mono">
                <FiLayers />
                Revisions: {docDetails.revisions?.length || 1}
              </div>
            </div>
          )}
        </div>

        {fetching ? (
          <div className="bg-white p-16 rounded-2xl border border-slate-100 shadow-sm text-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <span className="text-sm text-slate-500 font-medium">Fetching document details...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
            {/* Form Column (Left 3) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Document Title */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                      Document Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition ${
                        titleError ? "border-red-300 focus:ring-red-500/10 focus:border-red-500" : "border-slate-200"
                      }`}
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (e.target.value.trim()) setTitleError("");
                      }}
                    />
                    {titleError && (
                      <p className="text-red-500 text-xs mt-1.5 font-medium">{titleError}</p>
                    )}
                  </div>

                  {/* Document Category Dropdown */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                        Document Category <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => navigate("../categoryManager")}
                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline transition"
                      >
                        Manage Categories
                      </button>
                    </div>
                    {categoriesLoading ? (
                      <div className="w-full h-10 bg-slate-100 rounded-xl animate-pulse" />
                    ) : (
                      <select
                        className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition cursor-pointer ${
                          categoryError ? "border-red-300 focus:ring-red-500/10 focus:border-red-500" : "border-slate-200"
                        }`}
                        value={category}
                        onChange={(e) => {
                          setCategory(e.target.value);
                          if (e.target.value.trim()) setCategoryError("");
                        }}
                      >
                        <option value="">Select a category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.name}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    )}
                    {categoryError && (
                      <p className="text-red-500 text-xs mt-1.5 font-medium">{categoryError}</p>
                    )}
                  </div>
                </div>

                {/* Editor */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Document Content <span className="text-red-500">*</span>
                  </label>
                  <div className={`rounded-2xl overflow-hidden border ${
                    contentError ? "border-red-300" : "border-slate-200 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500"
                  }`}>
                    <Editor
                      apiKey={tinyApiKey}
                      init={{
                        height: 500,
                        menubar: true,
                        branding: false,
                        plugins:
                          "advlist autolink lists link image charmap print preview anchor " +
                          "searchreplace visualblocks code fullscreen " +
                          "insertdatetime media table paste code help wordcount",
                        toolbar:
                          "undo redo | formatselect | bold italic underline strikethrough | " +
                          "alignleft aligncenter alignright alignjustify | " +
                          "bullist numlist outdent indent | " +
                          "link image media table | " +
                          "forecolor backcolor removeformat | " +
                          "charmap fullscreen preview code help",
                        image_advtab: true,
                        content_style:
                          "body { font-family:-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size:14px; line-height:1.6; color:#334155; padding: 12px; }",
                      }}
                      value={content}
                      onEditorChange={(content) => {
                        setContent(content);
                        if (content.replace(/<[^>]*>/g, "").trim()) {
                          setContentError("");
                        }
                      }}
                    />
                  </div>
                  {contentError && (
                    <p className="text-red-500 text-xs mt-1.5 font-medium">{contentError}</p>
                  )}
                </div>
              </div>

              {/* Revision Change Summary Card */}
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                    Change Summary <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-slate-400 mb-3">
                    Briefly describe what changes were made in this edit to help your team audit updates.
                  </p>
                  <input
                    type="text"
                    placeholder="e.g. Fixed typos in getting started section, added API schema table"
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition ${
                      changeSummaryError ? "border-red-300 focus:ring-red-500/10 focus:border-red-500" : "border-slate-200"
                    }`}
                    value={changeSummary}
                    onChange={(e) => {
                      setChangeSummary(e.target.value);
                      if (e.target.value.trim()) setChangeSummaryError("");
                    }}
                  />
                  {changeSummaryError && (
                    <p className="text-red-500 text-xs mt-1.5 font-medium">{changeSummaryError}</p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 active:bg-slate-100 transition duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateDoc}
                  disabled={loading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:shadow transition duration-150"
                >
                  {loading ? (
                    "Saving Changes..."
                  ) : (
                    <>
                      <FiCheck />
                      Save & Publish Update
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Quick Tips Column (Right 1) */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex items-center gap-2 text-slate-800 font-bold text-sm mb-4 border-b border-slate-100 pb-3">
                  <FiInfo className="text-blue-500 text-base" />
                  <span>Revision Auditing</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">
                  BVM Project Manager tracks document states via <strong>version increments</strong>. 
                  Every save increases the version count by 1 and records your edit to the revision timeline.
                  <br /><br />
                  Writing a clear <strong>change summary</strong> makes it easy to compare changes across audits.
                </p>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
